import type { WatchStopHandle } from 'vue'
import { watch } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import type { DocumentTypeValue } from './constants'
import {
  CACHED_TYPES,
  DocumentTypeValues,
  DocumentTypeTranslationKeys,
} from './constants'
import type { ParamsValue } from './types'
import { getQueryParamId } from '@app/utils/url-helpers'

/**
 * Helper function to create a watch that triggers TipTap suggestion updates
 *
 * TipTap's suggestion plugin doesn't automatically react to external Vue prop changes.
 * The suggestion renderers use getter functions to access reactive props, but TipTap
 * doesn't know when these values change. We need to manually call forceUpdate() which:
 * 1. Re-evaluates the getter functions with fresh prop values
 * 2. Calls updateProps() on the VueRenderer to update the component
 *
 * This watch monitors the reactive props and triggers the update when they change.
 */
export const getDocumentTypeLabels = (): Record<DocumentTypeValue, string> => {
  const I18n = useI18n()

  return {
    [DocumentTypeValues.ACCOUNT_ENTRY]: I18n.t(
      DocumentTypeTranslationKeys[DocumentTypeValues.ACCOUNT_ENTRY]
    ),
    [DocumentTypeValues.RECONCILIATION_TEMPLATE]: I18n.t(
      DocumentTypeTranslationKeys[DocumentTypeValues.RECONCILIATION_TEMPLATE]
    ),
    [DocumentTypeValues.PERMANENT_DOCUMENT]: I18n.t(
      DocumentTypeTranslationKeys[DocumentTypeValues.PERMANENT_DOCUMENT]
    ),
    [DocumentTypeValues.PERIOD_SPECIFIC_DOCUMENT]: I18n.t(
      DocumentTypeTranslationKeys[DocumentTypeValues.PERIOD_SPECIFIC_DOCUMENT]
    ),
    [DocumentTypeValues.EXPORT]: I18n.t(
      DocumentTypeTranslationKeys[DocumentTypeValues.EXPORT]
    ),
  }
}

export const getCompanyId = (): ParamsValue =>
  window.Silverfin.currentCompanyId || getQueryParamId('activeCompanyId')

export const isCached = (type: DocumentTypeValue | null): boolean =>
  type !== null && CACHED_TYPES.includes(type)

export const buildBaseParams = (
  params: Record<string, ParamsValue>
): Record<string, ParamsValue> => ({
  firm_id: window.Silverfin.currentFirmId,
  company_id: getCompanyId(),
  ...params,
  _options: true,
})

export const createSuggestionWatch = (
  deps: () => unknown[],
  renderer: { forceUpdate?: () => void }
): WatchStopHandle => {
  return watch(
    deps,
    () => {
      // Manually update the VueRenderer component with fresh prop values
      // This is the direct, reliable way to sync external prop changes to the popup
      renderer.forceUpdate?.()
    },
    { deep: true }
  )
}
